// ProfileZone — the six raster blocks on the Organisation brief (BUILD-BRIEF-R3 §4).
//
// One `.fgroup` per block with that block's coverage beside its head, and one row per cell. A
// value's evidence opens as a `blockquote.ev` with its `.ev-src`.
//
// THE ZONE HAS NO VERB (BUILD-BRIEF-R4 §4). The raster is filled by a `deep_research` run, which
// the Organisation mast starts as its one accent verb; what this head carries instead is the LAST
// RESEARCH — which run, when, and how many cells it filed. The desk's steps and the proposals in
// Review are the receipt now.
//
// THE THREE RULES THIS FILE MUST NOT BREAK:
//   · A COMPUTED CELL CARRIES NO MARK. The triggers block is arithmetic over signals; ◇ and ▪
//     are claims, and arithmetic makes none.
//   · "not collected" IS NOT ZERO. A cell nobody filled renders `fv--unknown` with the words
//     "not collected"; a counted zero — and a counted `false` — renders `fv--nil`. They must
//     never look the same.
//   · AN ESTIMATE SAYS SO. `method === 'estimated'` carries a `.tag--ghost` reading "estimated"
//     with its confidence, never the same dress as a value read off a page.
//
// THE LABELS COME FROM THE RASTER, in the console's current language, through `labelFor`.
// Nothing here retypes a block, a cell or a value name: the raster is the one document, and a
// second spelling of `deficience_intellectuelle` in a page would be a second source of truth.
//
// THE ZONE LOADS ITSELF. The Organisation page passes an id and, optionally, takes the loaded
// profile back through `onProfile` so its masthead can print the tier without a second call.
import { useCallback, useEffect, useState } from 'react';
import EmptyState from './EmptyState.js';
import { documentHref, readDocuments, readProposable, swissTicks, type ProfileDocument } from './DocumentsZone.js';
import { Att, Ev, Fig, Lnk, Ref, Sep, Sheet, Tag, Zone, ZoneHead } from './PageKit.js';
import { useLocale, useT } from '../i18n/index.js';
import { href, ROUTES } from '../routes.js';
import { getProfile } from '../api/profile.js';
import { getSnapshot } from '../api/snapshots.js';
import { QUALIFIER_SIGN, RASTER, REF_NONE, labelFor, type NumericQualifier, type RasterCell, type RasterLang } from '../../shared/raster.v1.js';
import type { EvidenceKind, OrgTier, ProfileCellValue, ProfileLastResearch, ProfileResponse, Snapshot } from '../../shared/types.js';
import './ProfileZone.de.js';
import './ProfileZone.fr.js';

export interface ProfileZoneProps {
  organisationId: number;
  /** Hands the loaded profile back, so the masthead's tier tag costs no second request. */
  onProfile?: (profile: ProfileResponse | null) => void;
}

/** A tone is a CONDITION, never a category hue. Only `target` and `watch` want the operator. */
export const TIER_TONE: Record<OrgTier, 'ok' | 'attend' | 'neutral'> = {
  target: 'ok',
  watch: 'attend',
  park: 'neutral',
  exclude: 'neutral',
};

export default function ProfileZone({ organisationId, onProfile }: ProfileZoneProps) {
  const t = useT();
  const { lang, fmtDate, fmtNum } = useLocale();

  const [profile, setProfile] = useState<ProfileResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [openCell, setOpenCell] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getProfile(organisationId);
      setProfile(res);
      setError(null);
      onProfile?.(res);
    } catch (err) {
      setProfile(null);
      // The server's own sentence, never a paraphrase: while `profile-core` has not built the
      // endpoint this is its 501 explaining exactly what is missing.
      setError(err instanceof Error ? err.message : String(err));
      onProfile?.(null);
    } finally {
      setLoading(false);
    }
  }, [organisationId, onProfile]);

  useEffect(() => { void load(); }, [load]);

  const cov = profile?.coverage ?? null;
  const score = profile?.score ?? null;
  const anythingFilled = (cov?.filled ?? 0) > 0;

  // THE LAST RESEARCH. Read straight off the profile: which `deep_research` run last worked this
  // organisation, when it ended and how many raster cells it filed. The count is arithmetic over
  // that run's own `fact` proposals — a computed count, so it carries no claim mark — and `null`
  // means no run was ever started, which reads "not collected" and never as a zero.
  const lastResearch = profile?.last_research ?? null;
  const documents = readDocuments(profile);
  // HOW MANY CELLS A RUN MAY PROPOSE — the denominator the coverage figure counts against
  // (§2.6). It is not `coverage.defined`: that is the smaller set of comparable cells the
  // percentage is measured over, and the two answer different questions. A server that does not
  // send it yet leaves the head measuring against the comparable set, which is honest too.
  const proposable = readProposable(profile);

  return (
    <Zone id="profile">
      <ZoneHead
        label={t('Profile')}
        count={cov
          ? (
            <span title={proposable !== null
              ? t('{n} of the cells a fill may propose are filled; the percentage beside it is measured over the {d} comparable cells.', { n: cov.filled, d: cov.defined })
              : t('Measured over the comparable cells: this server does not report how many a fill may propose.')}
            >
              <Fig>{cov.filled}</Fig> {t('of')} <Fig>{proposable ?? cov.defined}</Fig> {t('cells')}
            </span>
          )
          : undefined}
        note={cov
          ? (
            <>
              {t('{pct}% of the comparable cells · raster {v}', { pct: cov.pct, v: profile?.raster_version ?? '' })}
              {' '}<Sep />{' '}
              <LastResearchLine research={lastResearch} fmtDate={fmtDate} fmtNum={fmtNum} />
            </>
          )
          : t('one standardised raster of comparable cells')}
        endNote={score
          ? <>
              <Tag tone={TIER_TONE[score.tier]} title={t('fit × timing × access')}>{t(score.tier)}</Tag>
              {' '}<Fig title={t('fit × timing × access')}>{score.composite}</Fig>
              {' '}<Sep />{' '}
              {t('fit')} <Fig>{score.fit}</Fig> · {t('timing')} <Fig>{score.timing}</Fig> · {t('access')} <Fig>{score.access}</Fig>
            </>
          : profile
            ? <span className="fv--unknown">{t('not scored')}</span>
            : undefined}
      />

      {error ? (
        <EmptyState kind="error" title={t('The profile did not answer')} detail={<>GET /api/profile/{organisationId} — {error}</>} onRetry={() => { void load(); }} />
      ) : loading || !profile ? (
        <p className="zone-empty">{t('Loading…')}</p>
      ) : (
        <>
          {!anythingFilled && (
            <p className="zone-empty">
              {t('No cell of the raster is filled for this organisation. "Research" on the masthead starts a run; every value it reads comes back as a proposal with its verbatim quote, and nothing is on the record until you hold it.')}
            </p>
          )}
          <Sheet pad className="facts">
            {RASTER.map((block) => {
              const view = profile.blocks.find((b) => b.block === block.key) ?? null;
              const cells = view?.cells ?? [];
              return (
                <div className="fgroup" key={block.key}>
                  <div className="fgroup-h u-label">
                    {labelFor(lang as RasterLang, block.key)}
                    {block.computed ? (
                      <span className="zone-note"> · {t('computed nightly · no claim mark')}</span>
                    ) : view ? (
                      <span className="zone-note"> · <Fig>{`${view.coverage.filled} / ${view.coverage.defined}`}</Fig></span>
                    ) : null}
                  </div>
                  {block.cells.map((cell) => {
                    const key = `profile.${block.key}.${cell.key}`;
                    const value = cells.find((c) => c.key === key) ?? null;
                    return (
                      <CellRow
                        key={key}
                        blockKey={block.key}
                        cell={cell}
                        value={value}
                        open={openCell === key}
                        onToggle={() => setOpenCell((k) => (k === key ? null : key))}
                        lang={lang as RasterLang}
                        fmtDate={fmtDate}
                        fmtNum={fmtNum}
                        documents={documents}
                      />
                    );
                  })}
                </div>
              );
            })}
          </Sheet>
        </>
      )}
    </Zone>
  );
}

// ── the last research ───────────────────────────────────────────────────────

/**
 * "last research RUN-0007 · 04.09.2026 · 18 cells filed" — the one line the Profile head carries
 * where the receipt used to be (BUILD-BRIEF-R4 §4).
 *
 * THREE RULES IT KEEPS. The ref is an IDENTITY and reads mono, as a link to the run's own desk,
 * which is the account now — its steps, its ledger and what it filed. The date and the count are
 * QUANTITIES and read as figures. The count is arithmetic over that run's `fact` proposals, so it
 * carries NO claim mark; a run that filed nothing is a counted zero (`fv--nil`) and a raster
 * nobody has ever researched is "not collected" (`fv--unknown`). The two never look the same.
 */
export function LastResearchLine({ research, fmtDate, fmtNum }: {
  research: ProfileLastResearch | null;
  fmtDate: (iso: string | null | undefined) => string;
  fmtNum: (n: number | string | null | undefined) => string;
}) {
  const t = useT();
  if (!research) {
    return (
      <span title={t('No research run has been started on this organisation yet.')}>
        {t('last research')}{' '}<span className="fv--unknown">{t('not collected')}</span>
      </span>
    );
  }
  return (
    <span title={t('The run that last worked this raster — its steps, its ledger and every cell it filed')}>
      {t('last research')}{' '}
      <Lnk href={href(ROUTES.run({ id: research.run_id }))}><Ref>{research.run_ref}</Ref></Lnk>
      {' '}<Sep />{' '}
      {research.at
        ? <Fig>{fmtDate(research.at)}</Fig>
        : <span className="fv--unknown">{t('still running')}</span>}
      {' '}<Sep />{' '}
      <Fig className={research.cells_filed === 0 ? 'fv--nil' : undefined}>{fmtNum(research.cells_filed)}</Fig>
      {' '}{t('cells filed')}
    </span>
  );
}

// ── evidence source: a link and a fetch date, not a bare ref ───────────────
//
// A cell's `.ev-src` used to print `<Ref>{evidence_ref}</Ref>` — the snapshot's bare id, which
// names nothing a reader can open. `evidence_ref` for `evidence_kind === 'snapshot'` IS a
// snapshot id: `GET /api/snapshots/:id` resolves it to the same `final_url` + `dd.mm.yyyy` fetch
// date DossierZone already shows beside a dossier citation (see `CitedText` there); this makes
// the profile brief and the compare sheet say the same thing. A module-level cache means the
// same snapshot opened twice — once on the brief, once in a comparison — is fetched once.
const snapshotCache = new Map<number, Snapshot | null>();

function useSnapshotsFor(ids: number[]): Map<number, Snapshot> {
  const key = ids.filter((id) => Number.isFinite(id)).sort((a, b) => a - b).join(',');
  const [, rerender] = useState(0);
  useEffect(() => {
    const wanted = key ? key.split(',').map(Number) : [];
    const missing = wanted.filter((id) => !snapshotCache.has(id));
    if (!missing.length) return;
    let cancelled = false;
    Promise.all(missing.map((id) => getSnapshot(id)
      .then((s) => { snapshotCache.set(id, s); })
      .catch(() => { snapshotCache.set(id, null); })))
      .then(() => { if (!cancelled) rerender((n) => n + 1); });
    return () => { cancelled = true; };
  }, [key]);
  const out = new Map<number, Snapshot>();
  for (const id of key ? key.split(',').map(Number) : []) {
    const s = snapshotCache.get(id);
    if (s) out.set(id, s);
  }
  return out;
}

/** The `.ev-src` of one piece of evidence: a link to the snapshot's URL and its fetch date when
 *  the evidence is a snapshot the record can resolve, the bare ref otherwise (a mail message, an
 *  operator note, or a snapshot the fetch has not answered for yet).
 *
 *  WHEN THE EVIDENCE IS A PDF THE RUN KEPT, the source URL is not enough. The third party can
 *  move or restate that file tomorrow; what the quote actually rests on is the copy the run read
 *  and the record stored, so the `.ev-src` carries a second link — "Open" — to
 *  `GET /api/snapshots/:id/pdf`, the stored bytes. Three ways to know a citation is a document:
 *  the profile listed it under `documents`, the snapshot row kept a `pdf_path`, or the URL is
 *  plainly a .pdf. The first also gives the URL and the date without a second fetch, which is
 *  why a document citation renders even before `GET /api/snapshots/:id` has answered. */
export function EvidenceSrc({ evidenceKind, evidenceRef, fmtDate, documents, asOf }: {
  evidenceKind: EvidenceKind | null;
  evidenceRef: string | null;
  fmtDate: (iso: string | null | undefined) => string;
  /** The kept documents of the organisation being read, when the caller has them. */
  documents?: ProfileDocument[] | null;
  /** The value's own `as_of` — the day the citation was read. A `url` citation resolves no
   *  snapshot row, so this is the only date it has; without it the line says so. */
  asOf?: string | null;
}) {
  const t = useT();
  const id = evidenceKind === 'snapshot' && evidenceRef ? Number(evidenceRef) : NaN;
  const snapshots = useSnapshotsFor(Number.isFinite(id) ? [id] : []);
  if (!evidenceRef) return <>{t('no source ref recorded')}</>;
  const snap = Number.isFinite(id) ? snapshots.get(id) : undefined;
  const doc = Number.isFinite(id) ? (documents ?? []).find((d) => d.snapshot_id === id) ?? null : null;
  // A CITATION THAT IS A URL IS STILL A CITATION (P2): a register or LINDAS pre-fill keeps no
  // snapshot row, and rendering its ref as bare text drops both the link and the date the run
  // read it. It gets the same line as a snapshot — the page, and the day — minus the kept copy.
  const plainUrl = !snap && !doc && /^https?:\/\//i.test(evidenceRef);
  if (!snap && !doc && !plainUrl) return <Ref>{evidenceRef}</Ref>;
  const url = snap ? (snap.final_url ?? snap.url) : doc ? doc.url : evidenceRef;
  const fetchedAt = snap?.fetched_at ?? doc?.fetched_at ?? asOf ?? null;
  const kept = Boolean(snap && (snap as unknown as { pdf_path?: string | null }).pdf_path);
  const isDocument = Boolean(doc) || kept || /\.pdf(\?|#|$)/i.test(url);
  return (
    <>
      <a className="lnk" href={url} target="_blank" rel="noreferrer noopener"><Ref>{url}</Ref></a>
      {' '}<Sep />{' '}
      {fetchedAt ? <Fig>{fmtDate(fetchedAt)}</Fig> : <span className="fv--unknown">{t('fetch date not recorded')}</span>}
      {isDocument && Number.isFinite(id) && (
        <>
          {' '}<Sep />{' '}
          <a
            className="lnk"
            href={documentHref(id)}
            target="_blank"
            rel="noreferrer noopener"
            title={t('Open the kept PDF as the run read it')}
          >
            {t('Open')}
          </a>
        </>
      )}
    </>
  );
}

// ── one cell ────────────────────────────────────────────────────────────────

function CellRow({ blockKey, cell, value, open, onToggle, lang, fmtDate, fmtNum, documents }: {
  blockKey: string;
  cell: RasterCell;
  value: ProfileCellValue | null;
  open: boolean;
  onToggle: () => void;
  lang: RasterLang;
  fmtDate: (iso: string | null | undefined) => string;
  fmtNum: (n: number | string | null | undefined) => string;
  documents: ProfileDocument[] | null;
}) {
  const t = useT();
  const hasEvidence = !!(value?.quote || value?.evidence_ref);
  return (
    <div className="fact">
      <span className="fk" title={cell.sources_hint}>{labelFor(lang, blockKey, cell.key)}</span>
      <span>
        <CellValue value={value} cell={cell} blockKey={blockKey} lang={lang} fmtNum={fmtNum} />
        {hasEvidence && (
          <>
            {' '}
            <Lnk onClick={onToggle} title={t('Show the verbatim evidence for this cell')}>
              {open ? t('hide evidence') : t('evidence')}
            </Lnk>
          </>
        )}
        {open && hasEvidence && (
          <Ev src={<EvidenceSrc evidenceKind={value?.evidence_kind ?? null} evidenceRef={value?.evidence_ref ?? null} fmtDate={fmtDate} documents={documents} asOf={value?.as_of ?? null} />}>
            {value?.quote ?? t('The evidence names a source but keeps no verbatim passage.')}
          </Ev>
        )}
      </span>
      <span className="fmeta">
        {value?.method === 'estimated' && value.confidence != null && (
          <>{t('confidence')} <Fig>{value.confidence.toFixed(2)}</Fig> </>
        )}
        {value?.as_of ? <Fig>{fmtDate(value.as_of)}</Fig> : null}
      </span>
    </div>
  );
}

/**
 * The value itself, with its mark. THIS FUNCTION IS THE ONE PLACE the four states are dressed,
 * and CompareSheet renders through it too, so a cell can never look one way on the brief and
 * another way in the comparison.
 */
const fmtDateShort = (iso: string): string => { const d = new Date(iso); return Number.isNaN(d.getTime()) ? iso : `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`; };

export function CellValue({ value, cell, blockKey, lang, fmtNum }: {
  value: ProfileCellValue | null;
  cell: RasterCell;
  blockKey: string;
  lang: RasterLang;
  fmtNum: (n: number | string | null | undefined) => string;
}) {
  const t = useT();

  // Five states, never dressed alike: filled · estimated · not found (searched, absent — a verdict
  // with a date) · not applicable · not collected (nobody looked).
  if (value && value.method === 'not_found') {
    return <span className="fv--nil" title={t('A run searched for this cell and found nothing; later runs leave it alone unless asked to refresh.')}>{t('not found')}{value.as_of ? <> · <Fig>{fmtDateShort(value.as_of)}</Fig></> : null}</span>;
  }
  if (!value || value.value === null || value.value.trim() === '') {
    return <span className="fv--unknown">{t('not collected')}</span>;
  }

  const computed = value.method === 'computed';
  const estimated = value.method === 'estimated';
  // A reference cell shows the row it points at, never its bare id (the "123" of 2026-09-04).
  const isRef = cell.type === 'person_ref' || cell.type === 'organisation_ref';
  const text = isRef && value.value !== REF_NONE && value.value_label
    ? value.value_label
    : cellText(cell, blockKey, value.value, lang, fmtNum, t, value.qualifier ?? null);
  const nil = isCountedZero(cell, value.value);

  return (
    <>
      {/* Arithmetic makes no claim: a computed cell carries no ◇ and no ▪. */}
      {!computed && <><Att kind={value.held ? 'held' : 'proposed'} />{' '}</>}
      <span className={nil ? 'fv--nil' : undefined}>{text}</span>
      {estimated && (
        <> <Tag tone="ghost" title={t('The agent inferred this value; it was not read off a page.')}>{t('estimated')}</Tag></>
      )}
    </>
  );
}

/** A `false` boolean and a `0` count are COUNTED, and read as quietly as a zero should. */
export function isCountedZero(cell: RasterCell, raw: string): boolean {
  if (cell.type === 'bool') return raw === 'false';
  if (cell.type === 'int' || cell.type === 'number') return Number(raw) === 0;
  // A reference that is `none` — an independent association has no parent — is counted and absent.
  if (cell.type === 'person_ref' || cell.type === 'organisation_ref') return raw === REF_NONE;
  return false;
}

/**
 * A stored cell value as a reader sees it. Enum, band and multi values come back through
 * `labelFor` in the console's language; a quantity keeps its unit; a reference stays a
 * reference. Never mono on a quantity, never a raw enum token on the page.
 */
/** The units that are a CURRENCY and not a measure. A raster cell carrying one holds money, and
 *  money reads 'CHF 12’500' — the code first — wherever it is printed. */
const CURRENCIES = new Set(['CHF', 'EUR', 'USD', 'GBP']);
export const isCurrency = (unit: string | null | undefined): boolean => CURRENCIES.has((unit ?? '').toUpperCase());

export function cellText(
  cell: RasterCell,
  blockKey: string,
  raw: string,
  lang: RasterLang,
  fmtNum: (n: number | string | null | undefined) => string,
  t: (key: string, vars?: Record<string, string | number>) => string,
  qualifier: NumericQualifier | null = null,
): string {
  switch (cell.type) {
    case 'enum':
    case 'band':
      return labelFor(lang, blockKey, cell.key, raw);
    case 'enum_multi':
      return raw.split(',').map((v) => labelFor(lang, blockKey, cell.key, v.trim())).join(' · ');
    case 'bool':
      return raw === 'true' ? t('yes') : raw === 'false' ? t('no') : raw;
    case 'int':
    case 'number': {
      // The figure voice groups with the typographic apostrophe in every browser — `1’170`.
      const n = swissTicks(fmtNum(raw));
      // HOW EXACT: a floor reads `≥ 116 places`, an approximation `≈ 250 persons`. The sign is
      // part of the figure's voice, never a tag — the number is what the page said.
      const sign = qualifier ? `${QUALIFIER_SIGN[qualifier]} ` : '';
      if (!cell.unit) return `${sign}${n}`;
      // MONEY READS 'CHF 12’500' (CLAUDE.md, and `fmtMoney` in src/shared/format.ts): the
      // currency leads its amount. Every other unit is a measure and follows it — `1’170 FTE`.
      return isCurrency(cell.unit) ? `${sign}${cell.unit} ${n}` : `${sign}${n} ${cell.unit}`;
    }
    case 'year':
      return raw;
    case 'month':
      return raw;
    case 'person_ref':
    case 'organisation_ref':
      // `none` is a counted absence and reads as one; an id without its label is shown as it is.
      return raw === REF_NONE ? t('none') : raw;
    default:
      return raw;
  }
}
